import React, { useState, useEffect } from "react";
import {
  Box, Typography, IconButton, Button, Stack
} from "@mui/material";
import { ArrowBackIosNew, ArrowForwardIos } from "@mui/icons-material";
import { useParams } from "react-router-dom";
import {
  fetchRoomsCalendar,
  createRatePrice,
  updateAvailability,
  updateStatus,
} from "./inventoryService.js";
import InventoryGrid from "./InventoryGrid.jsx";
import axios from "axios";
import BulkEditDrawer from "./BulkEditDrawer";



const Calendar = ({ setActivePage }) => {
  const hotelParams = useParams();
  const hotelId = hotelParams.hotelId;

  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [rooms, setRooms] = useState([]);
  const [startIndex, setStartIndex] = useState(0);
  // State
const [bulkEditDrawer, setBulkEditDrawer] = useState({ open: false, room: null });


  const daysPerPage = 10;

  const [ratePlansData, setRatePlansData] = useState([]);


  // Sélection
  const [selectionStart, setSelectionStart] = useState(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectedDays, setSelectedDays] = useState({ tarif: [], inventory: [], status: [] });
  const [editType, setEditType] = useState(null);
  const [editModeRoom, setEditModeRoom] = useState(null);
  const [editRatePlan, setEditRatePlan] = useState(null);
  const [newValue, setNewValue] = useState("");

  // Bulk drawer
  const [bulkDrawer, setBulkDrawer] = useState({ open: false, type: null, roomId: null });
  const [bulkValue, setBulkValue] = useState("");

  // ─── Utils ───
  const parseDate = (dateStr) => {
    const [y, m, d] = dateStr.split("-");
    return new Date(y, m - 1, d);
  };

  const formatDate = (date) => new Date(date).toLocaleDateString("sv-SE");

  const addOneDay = (dateStr) => {
    const d = parseDate(dateStr);
    d.setDate(d.getDate() + 1);
    return formatDate(d);
  };

  const getDaysOfMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    return Array.from({ length: daysInMonth }, (_, i) => {
      return new Date(year, month, i + 1).toLocaleDateString("sv-SE");
    });
  };

  const daysOfMonth = getDaysOfMonth();

  const getSelectedDateRange = () => {
    if (!editType) return null;
    const selectedIndexes = selectedDays[editType];
    if (!selectedIndexes?.length) return null;
    const realIndexes = selectedIndexes.map(i => startIndex + i);
    const sorted = [...realIndexes].sort((a, b) => a - b);
    const dateDebut = daysOfMonth[sorted[0]];
    const dateFin = addOneDay(daysOfMonth[sorted[sorted.length - 1]]);
    
    if (!dateDebut || !dateFin) return null;
    return { dateDebut, dateFin };
  };

  const [setupStatus, setSetupStatus] = useState(null);
// null | "no_rooms" | "no_availability" | "no_rateplan" | "no_price" | "ready"

  // ─── Fetch ───
  const fetchRooms = async () => {
    const start = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const end = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);

    try {
      // Fetch tout en parallèle
      const [calendarData, ratePlansRes] = await Promise.all([
        fetchRoomsCalendar(hotelId, formatDate(start), formatDate(end)),
        axios.get(`https://api.komorotel.com/api/rateplans/hotel/${hotelId}`, { withCredentials: true })
      ]);

      setRooms(calendarData);
      setRatePlansData(ratePlansRes.data);

      if (calendarData.length === 0) { setSetupStatus("no_rooms"); return; }

      const hasAvailability = calendarData.some(room =>
        room.days?.some(day => day.inventory > 0 || day.status === "opened")
      );
      if (!hasAvailability) { setSetupStatus("no_availability"); return; }

      // ✅ utilise ratePlansRes.data directement
      if (ratePlansRes.data.length === 0) { setSetupStatus("no_rateplan"); return; }

      const hasPrice = calendarData.some(room =>
        room.days?.some(day => day.rates?.some(r => r.price > 0))
      );
      if (!hasPrice) { setSetupStatus("no_price"); return; }

      setSetupStatus("ready");

    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchRooms(); }, [currentMonth, hotelId]);
  useEffect(() => { setStartIndex(0); }, [currentMonth]);

  useEffect(() => {
    const stop = () => { setIsSelecting(false); setSelectionStart(null); };
    window.addEventListener("mouseup", stop);
    return () => window.removeEventListener("mouseup", stop);
  }, []);

  // ─── Handlers ───
  const handleApply = async () => {
    if (newValue === "" || newValue === null) return;
    const range = getSelectedDateRange();
    if (!range) return;
    const { dateDebut, dateFin } = range;

    try {
      if (editType === "tarif" && editRatePlan) {
        await createRatePrice(
          editModeRoom,
          editRatePlan,
          parseInt(newValue),
          dateDebut,
          dateFin
        );
      }

      if (editType === "inventory") {
        await updateAvailability(editModeRoom, dateDebut, dateFin, parseInt(newValue));
      }

      // Reset
      setSelectedDays({ tarif: [], inventory: [], status: [] });
      setEditType(null);
      setEditModeRoom(null);
      setEditRatePlan(null);
      setNewValue("");
      await fetchRooms();

    } catch (err) {
      console.error(err);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    const selectedDates = selectedDays.status
      .map(i => daysOfMonth[startIndex + i])
      .sort((a, b) => parseDate(a) - parseDate(b));

    const dateDebut = selectedDates[0];
    const dateFin = addOneDay(selectedDates[selectedDates.length - 1]);

    try {
      await updateStatus(editModeRoom, dateDebut, dateFin, newStatus);
      setSelectedDays({ tarif: [], inventory: [], status: [] });
      setEditType(null);
      setEditModeRoom(null);
      await fetchRooms();
    } catch (err) {
      console.error(err);
    }
  };

  const handleApplyBulk = async () => {
    if (!bulkValue) return;
    const start = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const end = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
    const startDate = formatDate(start);
    const endDate = formatDate(end);
    const value = parseInt(bulkValue);
    if (isNaN(value) || value < 0) return;

    try {
      if (bulkDrawer.type === "inventory") {
        await updateAvailability(bulkDrawer.roomId, startDate, endDate, value);
      }

      if (bulkDrawer.type === "price" && bulkDrawer.ratePlanId) {
        if (bulkDrawer.type === "price" && bulkDrawer.ratePlanId) {
  
            // Vérifier si c'est le premier prix
            const existing = await axios.get(
              `https://api.komorotel.com/api/rateprices/${bulkDrawer.roomId}/${bulkDrawer.ratePlanId}`,
              { withCredentials: true }
            );

            const isFirstPrice = existing.data.length === 0;

            await createRatePrice(
              bulkDrawer.roomId,
              bulkDrawer.ratePlanId,
              value,
              isFirstPrice ? null : startDate,  // ✅ null si premier prix
              isFirstPrice ? null : endDate      // ✅ null si premier prix
            );
          }
      }

      setBulkDrawer({ open: false });
      setBulkValue("");
      await fetchRooms();
    } catch (err) {
      console.error(err);
    }
  };

  const changeMonth = (dir) => {
    const d = new Date(currentMonth);
    d.setMonth(d.getMonth() + dir);
    setCurrentMonth(d);
  };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#F8FAFC", p: 3 }}>

      {/* HEADER */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: "#0F172A", letterSpacing: "-0.5px" }}>
            Calendrier
          </Typography>
          <Typography sx={{ color: "#64748B", mt: 0.5 }}>
            Gérez les prix et disponibilités par chambre
          </Typography>
        </Box>
      </Stack>

       {/* SETUP BANNER */}
{setupStatus && setupStatus !== "ready" && (
  <Box sx={{
    mb: 4, p: 3,
    borderRadius: 3,
    border: "1px solid",
    borderColor: setupStatus === "no_rooms" ? "#FECACA" : setupStatus === "no_availability" ? "#FED7AA" : "#FEF08A",
    backgroundColor: setupStatus === "no_rooms" ? "#FEF2F2" : setupStatus === "no_availability" ? "#FFF7ED" : "#FEFCE8",
  }}>
    <Typography fontWeight={700} sx={{
      color: setupStatus === "no_rooms" ? "#991B1B" : setupStatus === "no_availability" ? "#92400E" : "#713F12"
    }}>
      {setupStatus === "no_rooms" && "🏨 Aucune chambre créée"}
      {setupStatus === "no_availability" && "📅 Disponibilité non configurée"}
      {setupStatus === "no_rateplan" && "🏷️ Aucun tarif créé"}
      {setupStatus === "no_price" && "💰 Aucun prix défini"}
    </Typography>

    <Typography fontSize={14} mt={0.5} sx={{
      color: setupStatus === "no_rooms" ? "#B91C1C" : "#92400E"
    }}>
      {setupStatus === "no_rooms" && "Commencez par créer une chambre dans la section Chambres."}
      {setupStatus === "no_availability" && "Configurez la disponibilité de votre chambre depuis la section Chambres."}
      {setupStatus === "no_rateplan" && "Créez un tarif depuis la section Tarifs pour rendre vos chambres réservables."}
      {setupStatus === "no_price" && "Définissez un prix pour au moins un tarif depuis le calendrier ou la section Tarifs."}
    </Typography>

    <Button
      variant="contained"
      size="small"
      sx={{ mt: 2, borderRadius: 2 }}
      onClick={() => {
        if (setupStatus === "no_rooms") setActivePage("rooms");
        if (setupStatus === "no_availability") setActivePage("rooms");
        if (setupStatus === "no_rateplan") setActivePage("tarifs");
        if (setupStatus === "no_price") setActivePage("tarifs");
      }}
    >
      {setupStatus === "no_rooms" && "Créer une chambre →"}
      {setupStatus === "no_availability" && "Configurer la disponibilité →"}
      {setupStatus === "no_rateplan" && "Créer un tarif →"}
      {setupStatus === "no_price" && "Définir un prix →"}
    </Button>

  </Box>
)}

      {/* MONTH SELECTOR */}
      <Stack direction="row" alignItems="center" justifyContent="center" mb={4} spacing={3}>
        <IconButton onClick={() => changeMonth(-1)}>
          <ArrowBackIosNew fontSize="small" />
        </IconButton>
        <Typography sx={{ fontSize: "1.1rem", fontWeight: 600, color: "#0F172A", minWidth: 180, textAlign: "center" }}>
          {(() => {
            const m = currentMonth.toLocaleString("fr-FR", { month: "long", year: "numeric" });
            return m.charAt(0).toUpperCase() + m.slice(1);
          })()}
        </Typography>
        <IconButton onClick={() => changeMonth(1)}>
          <ArrowForwardIos fontSize="small" />
        </IconButton>
      </Stack>

      {/* DAY PAGINATION */}
      <Stack direction="row" justifyContent="center" spacing={3} mb={4}>
        <Button disabled={startIndex === 0} onClick={() => setStartIndex(p => p - daysPerPage)}>←</Button>
        <Typography fontWeight={600}>
          Jours {startIndex + 1} à {Math.min(startIndex + daysPerPage, daysOfMonth.length)}
        </Typography>
        <Button disabled={startIndex + daysPerPage >= daysOfMonth.length} onClick={() => setStartIndex(p => p + daysPerPage)}>→</Button>
      </Stack>

      {/* DATE HEADER */}
      <Box sx={{
        display: "grid",
        gridTemplateColumns: `180px repeat(${daysPerPage}, 1fr)`,
        mb: 2,
        border: "1px solid #E2E8F0",
        borderRadius: 3,
        overflow: "hidden",
        backgroundColor: "#F8FAFC",
      }}>
        <Box sx={{ borderRight: "1px solid #E2E8F0", px: 2, py: 2, fontWeight: 600 }}>
          Chambre
        </Box>
        {daysOfMonth.slice(startIndex, startIndex + daysPerPage).map((dateStr, index) => {
          const date = parseDate(dateStr);
          const isToday = dateStr === formatDate(new Date());
          const isWeekend = date.getDay() === 0 || date.getDay() === 6;
          return (
            <Box key={index} sx={{
              borderLeft: "1px solid #E2E8F0",
              px: 1, py: 2,
              textAlign: "center",
              backgroundColor: isToday ? "#EEF2FF" : isWeekend ? "#F1F5F9" : undefined,
            }}>
              <Typography fontWeight={600} fontSize={14}>{date.getDate()}</Typography>
              <Typography fontSize={11} sx={{ color: "#64748B" }}>
                {date.toLocaleDateString("fr-FR", { weekday: "short" })}
              </Typography>
            </Box>
          );
        })}
      </Box>
     

     {/* ROOMS — seulement si tout est configuré */}
      {(setupStatus === "ready" || setupStatus === "no_price") && (
        <Stack spacing={4}>
          {rooms.map((room) => (
            <Box key={room.roomId}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setBulkEditDrawer({ open: true, room })}
                sx={{ borderColor: "#E2E8F0", color: "#64748B", fontSize: 12 }}
              >
                Modifier stock & dispo en masse
              </Button>

              
              <InventoryGrid
                room={room}
                daysOfMonth={daysOfMonth}
                startIndex={startIndex}
                daysPerPage={daysPerPage}
                selectedDays={selectedDays}
                setSelectedDays={setSelectedDays}
                editType={editType}
                setEditType={setEditType}
                editModeRoom={editModeRoom}
                setEditModeRoom={setEditModeRoom}
                isSelecting={isSelecting}
                setIsSelecting={setIsSelecting}
                selectionStart={selectionStart}
                setSelectionStart={setSelectionStart}
                editRatePlan={editRatePlan}
                setEditRatePlan={setEditRatePlan}
                bulkDrawer={bulkDrawer}
                setBulkDrawer={setBulkDrawer}
                bulkValue={bulkValue}
                setBulkValue={setBulkValue}
                handleApplyBulk={handleApplyBulk}
                setActivePage={setActivePage}
                allRatePlans={ratePlansData} // ✅ nouveau
              />
            </Box>
          ))}
          <BulkEditDrawer
                isOpen={bulkEditDrawer.open}
                onClose={() => setBulkEditDrawer({ open: false, room: null })}
                room={bulkEditDrawer.room}
                onSuccess={() => {
                  fetchRooms();
                  setBulkEditDrawer({ open: false, room: null });
                }}
              />
        </Stack>
      )}
      {/* FLOATING ACTION BAR */}
      {editType && selectedDays[editType]?.length > 0 && (
        <Box sx={{
          position: "fixed",
          bottom: 30,
          left: "50%",
          transform: "translateX(-50%)",
          backgroundColor: "#FFFFFF",
          boxShadow: "0 10px 30px rgba(0,0,0,0.12)",
          borderRadius: 3,
          px: 4, py: 3,
          display: "flex",
          alignItems: "center",
          gap: 3,
          zIndex: 2000,
        }}>
          <Typography fontWeight={600}>
            {selectedDays[editType].length} jour(s) sélectionné(s)
          </Typography>

          {editType === "status" ? (
            <>
              <Button variant="contained" color="success" onClick={() => handleStatusUpdate("opened")}>
                Ouvrir
              </Button>
              <Button variant="contained" color="error" onClick={() => handleStatusUpdate("closed")}>
                Fermer
              </Button>
            </>
          ) : (
            <>
              <input
                type="number"
                placeholder={editType === "tarif" ? "Nouveau prix" : "Nouveau stock"}
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                style={{ padding: "8px 12px", borderRadius: "8px", border: "1px solid #CBD5E1", width: 140 }}
              />
              <Button variant="contained" onClick={handleApply}>
                Appliquer
              </Button>
            </>
          )}

          <Button variant="text" onClick={() => {
            setSelectedDays({ tarif: [], inventory: [], status: [] });
            setEditType(null);
            setEditModeRoom(null);
            setNewValue("");
          }}>
            Annuler
          </Button>
        </Box>
      )}

    </Box>
  );
};

export default Calendar;